var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { InteractiveObject } from "../../core/object/interactive-object.js";
import { SliceOrientation, getNrrdModule } from "./NrrdModule.js";
/**
 * Interactive Image Object.
 */
export class InteractiveImage extends InteractiveObject {
    /** Open mesh from file path. */
    openImageFromFile(theFilePath) {
        const isDone = this._nrrdModule.openImage(this._myObjId, theFilePath);
        if (!isDone) {
            return Promise.reject(new Error("Open failed"));
        }
        return Promise.resolve(true);
    }
    /** Empty constructor. */
    constructor(theModule, theId) {
        super(theModule, theId);
        this._nrrdModule = getNrrdModule(theModule);
    }
    /** Make a copy of existing object with new id */
    clone(theObjId = "") {
        let anObjId = this._myViewer.interactiveContext.cloneObjectList(this._myObjId, false);
        if (anObjId === "") {
            return null;
        }
        let aCopy = new InteractiveImage(this._myViewer, theObjId);
        return aCopy;
    }
    /** Return image name. */
    get imageName() {
        return this._nrrdModule != null ? this._nrrdModule.getImageName(this._myObjId) : "";
    }
    /** Return case side. */
    get caseSide() {
        return this._nrrdModule != null ? this._nrrdModule.getImageCaseSide(this._myObjId) : "";
    }
    /** Return image width height depth. */
    get dimensions() {
        return this._nrrdModule != null ? this._nrrdModule.getImageDimensions(this._myObjId)
            : { x: 0, y: 0, z: 0 };
    }
    /** Return image width. */
    get width() { return this.dimensions.x; }
    /** Return image height. */
    get height() { return this.dimensions.y; }
    /** Return image depth. */
    get depth() { return this.dimensions.z; }
    /** Return number if image slices within active orientation. */
    get nbSlices() {
        return this._nrrdModule != null ? this._nrrdModule.getImageNbSlices(this._myObjId, -1) : 0;
    }
    /** Return number if image slices within axial orientation. */
    get nbAxialSlices() {
        return this._nrrdModule != null
            ? this._nrrdModule.getImageNbSlices(this._myObjId, SliceOrientation.Axial)
            : 0;
    }
    /** Return number if image slices within frontal orientation. */
    get nbFrontalSlices() {
        return this._nrrdModule != null
            ? this._nrrdModule.getImageNbSlices(this._myObjId, SliceOrientation.Frontal)
            : 0;
    }
    /** Return number if image slices within sagittal orientation. */
    get nbSagittalSlices() {
        return this._nrrdModule != null
            ? this._nrrdModule.getImageNbSlices(this._myObjId, SliceOrientation.Sagittal)
            : 0;
    }
    /** Return image active slice. */
    get activeSlice() {
        return this._nrrdModule != null ? this._nrrdModule.getImageSliceIndex(this._myObjId) : 0;
    }
    /** Set active slice index. */
    set activeSlice(theSlice) {
        this._nrrdModule.setImageSliceIndex(this._myObjId, theSlice != null ? theSlice : 0);
    }
    /** Get XRay lateral view flag. */
    get isXRayLateralView() {
        return this._nrrdModule.getXRayIsLateralView(this._myObjId);
    }
    /** Set XRay lateral view flag. */
    set isXRayLateralView(theIsLateral) {
        this._nrrdModule.setXRayRotationAngles(this._myObjId, this.rotationAngles, theIsLateral);
    }
    /** Get XRay orientation angles. */
    get rotationAngles() {
        return this._nrrdModule.getXRayRotationAngles(this._myObjId);
    }
    /** Set XRay orientation angles. */
    set rotationAngles(theAngles) {
        this._nrrdModule.setXRayRotationAngles(this._myObjId, theAngles, this.isXRayLateralView);
    }
    /** Return slice index closest to specific 3D point, or -1 if point is outside of 3d image. */
    findImageSliceIndex(thePnt) {
        return this._nrrdModule != null ? this._nrrdModule.findImageSliceIndex(this._myObjId, thePnt)
            : -1;
    }
    /** Return slice orientation. */
    get sliceOrientation() {
        return this._nrrdModule != null ? this._nrrdModule.getImageSliceOrientation(this._myObjId) : 0;
    }
    /** Set slice orientation. */
    set sliceOrientation(theOrient) {
        this._nrrdModule.setImageSliceOrientation(this._myObjId, theOrient != null ? theOrient : 0);
    }
    /** Return image axes. */
    get axes() { return this._nrrdModule.getImageAxes(this._myObjId); }
    /** Set image axes. */
    set axes(theAxes) { this._nrrdModule.setImageAxes(this._myObjId, theAxes); }
    /** Return image brightness factor; 1.0 by default (no correction). */
    get brightness() {
        return this._nrrdModule != null ? this._nrrdModule.getImageBrightness(this._myObjId) : 1.0;
    }
    /** Set image brightness factor. */
    set brightness(theFactor) {
        this._nrrdModule.setImageBrightness(this._myObjId, theFactor != null ? theFactor : 1.0);
    }
    /** Return gamma correction factor; 1.0 by default (no correction). */
    get gamma() {
        return this._nrrdModule != null ? this._nrrdModule.getImageGamma(this._myObjId) : 1.0;
    }
    /** Set gamma correction factor. */
    set gamma(theFactor) {
        this._nrrdModule.setImageGamma(this._myObjId, theFactor != null ? theFactor : 1.0);
    }
    /**
     * Open object from the given URL and register presentation(s) in the hidden (erased) state.
     * If Interactive Object was created with empty id, it will be generated from the given URL.
     *
     * The file will be loaded asynchronously.
     * To remove file from file system you should call [[OccViewer.unlinkFile]] if it is no longer needed.
     *
     * Depending on the file format, this method might read only a model structure without geometry - see [[InteractiveImage.loadUrlAsync]].
     * Warning! Displaying an object with not yet loaded geometry will produce errors!
     *
     * @param {string} theFileUrl the file URL address
     * @return {Promise<boolean>} FALSE if model cannot be opened
     */
    loadUrlAsync(theFileUrl) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this._myObjId === "") {
                this._myObjId = theFileUrl;
            }
            let aFilePath = this._myObjId.length > 0 && this._myObjId[0] == "/" ? this._myObjId
                : "/" + this._myObjId;
            yield this._myViewer.modelLoader.uploadUrl(theFileUrl, aFilePath, false);
            return this.openImageFromFile(aFilePath);
        });
    }
    /**
     * Load mask data
     * This method doesn't display the object in the Viewer - [[InteractiveObject.isVisible]]=true
     * should be called after loading data completion.
     * @param {string} theFileUrl the file URL address to read model from or empty string to load already opened model
     * @return {Promise<boolean>} FALSE if object was not found
     */
    loadMaskAsync() {
        return __awaiter(this, arguments, void 0, function* (theFileUrl = "") {
            let theFilePath = "";
            if (theFileUrl !== "") {
                theFilePath = yield this.preloadPathAsync(theFileUrl);
            }
            return yield this.loadMaskDataAsync(theFilePath);
        });
    }
    /**
     * preload file to viewer using url provided
     * @param theFilePath url to preload
     * @returns path to file
     */
    preloadPathAsync(theFileUrl) {
        return __awaiter(this, void 0, void 0, function* () {
            const aPaths = theFileUrl.split("/");
            const aFilePath = "/" + aPaths[aPaths.length - 1];
            yield this._myViewer.modelLoader.uploadUrl(theFileUrl, aFilePath, false);
            return aFilePath;
        });
    }
    /**
     * Load deferred data async.
     * @param theViewer module object
     * @return {Promise<boolean>} FALSE if model cannot be opened
     */
    loadDataAsync() {
        return new Promise((theResolve, theReject) => {
            const aCallable = {
                _myObj: undefined,
                resolve: function () {
                    theResolve(true);
                    this._myObj.delete();
                },
                reject: function (theErr) {
                    theReject(new Error(theErr));
                    this._myObj.delete();
                },
            };
            const aWasmPromise = this._myViewer.IPromise.implement(aCallable);
            aCallable._myObj = aWasmPromise;
            this._nrrdModule.loadData(this._myObjId, aWasmPromise);
        });
    }
    /**
     * Load deferred data async.
     * @param {string} theFilePath path to file
     * @return {Promise<boolean>} FALSE if model cannot be opened
     */
    loadMaskDataAsync(theFilePath) {
        return new Promise((theResolve, theReject) => {
            const aCallable = {
                _myObj: undefined,
                resolve: function () {
                    theResolve(true);
                    this._myObj.delete();
                },
                reject: function (theErr) {
                    theReject(new Error(theErr));
                    this._myObj.delete();
                },
            };
            const aWasmPromise = this._myViewer.IPromise.implement(aCallable);
            aCallable._myObj = aWasmPromise;
            this._nrrdModule.loadMaskData(this._myObjId, theFilePath, aWasmPromise);
        });
    }
}
